import styled from '@emotion/styled';
import { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import axios from '../../_utils/axios';
import { getData } from '../../redux/slices/dataTableSlice';
import { OrderJobTask } from '../../redux/slices/slice_types';
import SchemaForm from '../_common/SchemaForm/SchemaForm';
import Spinner from '../_common/Spinner';
import { useToast } from '../_common/Toast';
import { cleanTaskUpdatePayload } from '@/_utils/task';
import { selectCanWriteTasks } from '@/redux/selectors/authSelectors';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { isEqual } from 'lodash';

interface TaskDrawerExtraMetaProps {
  task: OrderJobTask | null;
  setActiveTask: (task: OrderJobTask) => void;
  onTaskChanged: () => void;
  onRefresh?: () => void;
}

const TaskDrawerExtraMeta = ({ task, onTaskChanged, setActiveTask, onRefresh }: TaskDrawerExtraMetaProps, ref: ForwardedRef<any>) => {
  const dispatch = useAppDispatch();
  const canWriteTasks = useAppSelector(selectCanWriteTasks);
  const { error, success } = useToast();
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState<any>();

  useImperativeHandle(ref, () => ({
    handleSubmit,
    data
  }));

  const handleChange = (changes: any) => {
    setData((val: any) => ({
      ...val,
      ...changes
    }));

    if (!!task?.extra_meta_schema && !!changes && !!data) {
      const { properties } = task.extra_meta_schema;
      const propertyNames = Object.keys(properties).filter((name) => {
        return properties[name].readOnly !== true;
      });
      const hasChanges = propertyNames.some((name) => {
        return !!changes[name] && !!changes[name].length && !isEqual(data[name], changes[name]);
      });

      if (hasChanges) {
        onTaskChanged();
      }
    }
  };

  useEffect(() => {
    if (task?.extra_meta) {
      setData((val: any) => ({
        ...val,
        ...task?.extra_meta
      }));
    }

    return () => setData({});
  }, [task]);

  const handleSubmit = () => {
    setSaving(true);
    if (!task?.id) return;
    const extra_meta = cleanTaskUpdatePayload(task, data);

    axios
      .post(`/core/tasks/${task.id}/`, { extra_meta })
      .then((res) => {
        if (!res.data?.success) throw new Error();
        else setActiveTask(res.data?.data || {});
        success('Updates saved.');
      })
      .catch((err) => error(err.response || 'There was an error retrieving the task.'))
      .finally(() => {
        setSaving(false);
        dispatch(getData());
      });
  };

  return (
    <TaskDrawerFormWrapper ref={ref} loading={+saving}>
      {saving && <Spinner />}
      <SchemaForm
        key={task?.id}
        schema={task?.extra_meta_schema}
        formData={data || {}}
        onChange={(data) => handleChange(data.formData)}
        formContext={{ task }}
        readonly={
          !canWriteTasks ||
          ['complete', 'completed', 'complete_with_errors', 'cancelled', 'cancelling', 'accepted', 'approved', 'approving'].includes(
            task?.status || ''
          )
        }
        onRefreshData={onRefresh}
      >
        <FooterSpacer />
      </SchemaForm>
    </TaskDrawerFormWrapper>
  );
};

export default forwardRef(TaskDrawerExtraMeta);

const TaskDrawerFormWrapper = styled.div<{ loading: number }>`
  width: 100%;
  position: relative;
  opacity: ${({ loading }) => (loading ? 0.5 : 1)};
`;

// added footer spacer so absolutely positioned sticky footer doesn't overlap form
const FooterSpacer = styled.div`
  height: 66px;
`;
